<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue';
import { consola } from 'consola';
import { useRoute } from '#imports';

import { useScrollTo , useGetWidth , useSafeArea } from '@shared/lib';
import { useAppearance, useInstitutionStore } from '@entities/institution';
import { coverStretchesFullBleed } from '@shared/config';
import { useModalStore , modalStateEnum } from '@shared/model';
import { useUserProfileStore, formatSelectedDeliveryAddress } from '@entities/user';

import { useCheckoutFormStore } from '@features/basket';

import { useUnreadNotifications } from '@features/notifications';
import { useSupportUnreadBadge } from '@features/support-chat';
import { useAuthStore } from '~/features/auth';


defineOptions({ name: 'AppHeader' });

const logger = consola.withTag('Hd');

const route = useRoute();
const router = useRouter();
const storeCheckoutForm = useCheckoutFormStore();
const storeInstitution = useInstitutionStore();
const { appearance, catalog: catalogAppearance } = useAppearance();
const headerAppearance = computed(() => appearance.value.header);

/**
 * Шапка без подложки.
 *
 * Прозрачная шапка имеет смысл только поверх обложки и только пока страница не
 * прокручена: ниже под шапкой едет каталог, и без фона её текст на карточках не
 * читается. Поэтому «без подложки» — это не «всегда прозрачная», а «прозрачная
 * на первом экране».
 */
const isAtPageTop = ref(true);

/**
 * Обложка «от края до края» начинается от самого верха страницы и уходит ПОД шапку
 * (см. widgets/site-cover). Непрозрачная полоса поверх такого баннера съедала бы его
 * верх и отменяла всю затею, поэтому здесь подложка снимается независимо от настройки
 * «Шапка → подложка»: это часть определения полноэкранного баннера, а не отдельный выбор.
 */
const isCoverFullBleed = computed(() => coverStretchesFullBleed(appearance.value.cover));

const isHeaderTransparent = computed(
  () =>
    (headerAppearance.value.surface === 'transparent' || isCoverFullBleed.value) &&
    appearance.value.cover.layout !== 'none' &&
    route.path === '/' &&
    isAtPageTop.value,
);

const trackPageTop = () => {
  isAtPageTop.value = window.scrollY < 24;
};

onMounted(() => {
  trackPageTop();
  window.addEventListener('scroll', trackPageTop, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('scroll', trackPageTop);
});
const storeModal = useModalStore();
const storeUserProfile = useUserProfileStore();
const storeAuth = useAuthStore();
const { width } = useGetWidth();
const config = useRuntimeConfig();

// Опции выбора филиала для UiKitSelect: { label: «Город, Улица», value: id }
const branchSelectOptions = computed(() =>
  (storeInstitution?.branchOptions ?? []).map((o) => ({ label: o.label, value: o.id })),
);

// Safe area для iOS и Android
const { headerStyle } = useSafeArea();

// Unread notifications
const { unreadCount: unreadNotificationsCount, fetchUnreadCount } = useUnreadNotifications();

// Непрочитанные ответы поддержки. Отдельной кнопки в шапке нет: счётчик
// складывается с уведомлениями в один бейдж на профиле (десктоп) и аватаре
// (мобильная шапка), а внутри ЛК видно, где именно непрочитано.
// Композабл нужен ещё и затем, чтобы сокет поднимался сразу после входа —
// иначе об ответе поддержки сообщал бы только пуш.
const { unreadCount: supportUnreadCount } = useSupportUnreadBadge(
  computed(() => Boolean(storeAuth.isLoggedIn)),
);

const headerUnreadCount = computed(
  () => unreadNotificationsCount.value + supportUnreadCount.value,
);
const hasHeaderUnread = computed(() => headerUnreadCount.value > 0);
const headerUnreadDisplay = computed(() =>
  headerUnreadCount.value > 99 ? '99+' : String(headerUnreadCount.value),
);

const { scrollTo } = useScrollTo();

/**
 * Поиск глобальный (шапка на всех страницах), а результаты — это меню на главной.
 * Поэтому при начале поиска уводим пользователя на меню, чтобы отфильтрованные товары были видны.
 * Сам фильтр уже живёт в store (debounce watch на searchQuery → fetchProducts с запросом).
 */
function goToMenu() {
  if (route.path !== '/') navigateTo('/');
}

watch(
  () => storeInstitution.searchQuery,
  (query, prev) => {
    if (query && !prev) goToMenu();
  },
);

const showHeader = ref(false);
const isFixed = ref(false);
const lastScrollPosition = ref(0);
const activeSection = ref<string | null>(null);
const navRef = ref<HTMLElement | null>(null);

/* ===== Высота шапки → CSS-переменная --app-header-h =====
 * Чтобы высота корзины (и др. блоков) считалась от РЕАЛЬНОЙ высоты шапки, а не магических чисел.
 * Обновляется через ResizeObserver — стабильно при ресайзе, баннерах, смене брейкпоинта. */
const headerEl = ref<HTMLElement | null>(null);
let headerResizeObserver: ResizeObserver | null = null;
function updateHeaderHeightVar() {
  if (typeof document === 'undefined') return;
  const h = headerEl.value?.offsetHeight ?? 0;
  if (h > 0) document.documentElement.style.setProperty('--app-header-h', `${h}px`);
  else document.documentElement.style.removeProperty('--app-header-h');
}

const imgAvatar = `${config.public.s3}/icon/avatar_men.png`;

/* ===== DEBUG HELPER ===== */
function debug(msg: string, ...args: any[]) {
  logger.debug('[HeaderNav]', msg, ...args);
}

/* ===== IntersectionObserver ===== */
let observer: IntersectionObserver | null = null;
function resetObserver() {
  if (observer) {
    observer.disconnect();
    observer = null;
    debug('resetObserver() called');
  }
}

function getCategories() {
  const categories = route.path === '/favorites' ? storeInstitution.favorites : storeInstitution.products;
  debug('getCategories() →', categories?.map?.((c) => c.to));
  return categories;
}

/* ===== Умное ожидание появления секций в DOM ===== */
function waitSectionsInDOM(categoryList: any[], maxTries = 20): Promise<void> {
  return new Promise((resolve) => {
    let tries = 0;
    function check() {
      const ready = categoryList.length > 0 && categoryList.every((cat) => !!document.querySelector(`[id="${cat.to}"]`));
      if (ready) {
        debug('waitSectionsInDOM: все секции есть в DOM');
        resolve();
      } else if (++tries < maxTries) {
        setTimeout(check, 100);
      } else {
        debug('waitSectionsInDOM: таймаут, не все секции появились');
        resolve();
      }
    }
    check();
  });
}

async function smartInitObserver() {
  resetObserver();
  const categories = getCategories();
  if (!categories || !categories.length) {
    debug('smartInitObserver: нет категорий');
    return;
  }
  await waitSectionsInDOM(categories);
  debug('smartInitObserver: DOM секций готов, запускаем observer');

  observer = new IntersectionObserver(
    (entries) => {
      debug('IntersectionObserver fired:', entries);
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          debug('Section visible:', entry.target.id);
          activeSection.value = entry.target.id;
        }
      });
    },
    { rootMargin: '-5% 0px -90% 0px', threshold: 0.01 },
  );

  categories.forEach(({ to }) => {
    if (!to) return;
    const el = document.querySelector<HTMLElement>(`[id="${to}"]`);
    if (el) {
      observer!.observe(el);
      debug('Observer observing:', el.id);
    } else {
      debug('Observer: section not found', to);
    }
  });

  if (!activeSection.value && categories[0]) {
    activeSection.value = categories[0].to ?? null;
    debug('smartInitObserver: set first activeSection', activeSection.value);
  }
}

/* ===== scrollToActiveNavItem ===== */
function scrollToActiveNavItem() {
  debug('scrollToActiveNavItem called, activeSection=', activeSection.value, 'navRef=', !!navRef.value);
  if (!activeSection.value || !navRef.value) return;

  nextTick(() => {
    // Если активна ПЕРВАЯ категория — прокручиваем навигацию в самое начало,
    // чтобы рядом с активным табом остались видны «Фильтр» и «Избранные».
    // На остальных категориях — прежнее поведение (активный таб к левому краю).
    const categories = getCategories();
    if (categories[0]?.to && categories[0].to === activeSection.value) {
      navRef.value!.scrollTo({ left: 0, behavior: 'smooth' });
      debug('Scrolled nav to start (first category active)');
      return;
    }

    const btn = navRef.value!.querySelector<HTMLElement>(`[data-link="${activeSection.value}"]`);
    if (btn) {
      navRef.value!.scrollTo({ left: btn.offsetLeft - 20, behavior: 'smooth' });
      debug('Scrolled nav to active button', btn.dataset.link, btn.offsetLeft);
    } else {
      debug('Active nav button not found in DOM for', activeSection.value);
    }
  });
}

/* ===== handleCategoryClick ===== */
function handleCategoryClick(sectionId: string | undefined) {
  if (!sectionId) return;
  debug('handleCategoryClick', sectionId);

  // В двухуровневом каталоге якорей других категорий на странице нет —
  // прокрутка никуда не приведёт, нужен переход внутрь категории.
  if (catalogAppearance.value.nesting === 'two-level') {
    activeSection.value = sectionId;
    router.push({ path: '/', query: { ...route.query, c: sectionId } });
    return;
  }

  scrollTo(`#${sectionId}`);
  activeSection.value = sectionId;
  history.replaceState(null, '', `#${sectionId}`);
}

/* ===== Header scroll logic ===== */
function makeCall() {
  debug('makeCall', storeInstitution.currentCity?.phone);
  window.location.href = `tel:${storeInstitution.currentCity?.phone}`;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- слушатель скролла временно отключён (см. закомментированные addEventListener), функцию оставляем
function handleScroll() {
  const y = window.scrollY;
  debug('handleScroll', y);

  if (y === 0) {
    showHeader.value = false;
    isFixed.value = false;
  } else {
    const threshold = 90;
    isFixed.value = y > threshold;
    showHeader.value = y < lastScrollPosition.value;
  }
  lastScrollPosition.value = y;
}

function isActive(id: string | undefined) {
  return id && activeSection.value === id;
}

/* ===== WATCHERS (очищаются на onUnmounted) ===== */
let stopCategoryWatch: (() => void) | null = null;
let stopRouteWatch: (() => void) | null = null;
let stopActiveSectionWatch: (() => void) | null = null;

onMounted(() => {
  debug('onMounted');
  // window.addEventListener('scroll', handleScroll, { passive: true });

  // Следим за высотой шапки → пишем в --app-header-h (хедер под v-if, поэтому через watch ref)
  if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
    headerResizeObserver = new ResizeObserver(() => updateHeaderHeightVar());
    watch(
      headerEl,
      (el) => {
        headerResizeObserver?.disconnect();
        if (el) headerResizeObserver?.observe(el);
        updateHeaderHeightVar();
      },
      { immediate: true },
    );
  }

  // Загружаем количество непрочитанных уведомлений для авторизованных пользователей
  if (storeAuth.isLoggedIn) {
    fetchUnreadCount();
  }

  stopCategoryWatch = watch(
    () => getCategories().length,
    (len, old) => {
      debug('watch: getCategories().length changed', len, 'old=', old);
      if (len && len !== old) nextTick(smartInitObserver);
    },
    { immediate: true },
  );

  stopRouteWatch = watch(
    () => route.path,
    (val) => {
      debug('watch: route.path changed', val);
      activeSection.value = null;
      nextTick(smartInitObserver);
    },
  );

  stopActiveSectionWatch = watch(activeSection, (val) => {
    debug('watch: activeSection changed', val);
    scrollToActiveNavItem();
  });

  // Отслеживаем авторизацию для загрузки уведомлений
  watch(
    () => storeAuth.isLoggedIn,
    (isLoggedIn) => {
      if (isLoggedIn) {
        fetchUnreadCount();
      }
    },
  );

  nextTick(smartInitObserver);
});

onUnmounted(() => {
  debug('onUnmounted: destroying header-nav');
  // window.removeEventListener('scroll', handleScroll);
  resetObserver();

  if (headerResizeObserver) {
    headerResizeObserver.disconnect();
    headerResizeObserver = null;
  }

  if (stopCategoryWatch) {
    stopCategoryWatch();
    debug('stopCategoryWatch() called');
  }
  if (stopRouteWatch) {
    stopRouteWatch();
    debug('stopRouteWatch() called');
  }
  if (stopActiveSectionWatch) {
    stopActiveSectionWatch();
    debug('stopActiveSectionWatch() called');
  }
});

// Обрезаем имя пользователя до 20 символов и добавляем "..." если нужно
const truncatedFirstName = computed(() => {
  const firstName = storeUserProfile?.getUser?.first_name || '';
  if (firstName.length <= 20) {
    return firstName;
  }
  return firstName.slice(0, 20) + '...';
});

/**
 * Возвращает адрес для отображения в зависимости от типа получения товара
 * - delivery: адрес доставки пользователя
 * - pickup/dine_in: адрес выбранного филиала заведения
 */
const displayAddress = computed(() => {
  const methodReceipt = storeCheckoutForm?.getForm?.method_receipt;

  if (methodReceipt === 'delivery') {
    // Для доставки показываем адрес доставки в едином формате: точка + подъезд, квартира
    return (
      formatSelectedDeliveryAddress(
        storeUserProfile?.selectedDeliveryAddress,
        storeCheckoutForm?.getForm?.delivery_address?.address,
      ) || null
    );
  } else {
    // Для самовывоза и еды в зале показываем адрес филиала
    const branch = storeInstitution?.selectedBranch;
    if (branch?.address) {
      // Сначала улица + дом, затем станица/город — единый порядок с селектом филиала
      return `${branch.address.street}, ${branch.address.city}`;
    }
    return null;
  }
});

/**
 * Возвращает placeholder текст в зависимости от типа получения товара
 */
const addressPlaceholder = computed(() => {
  const methodReceipt = storeCheckoutForm?.getForm?.method_receipt;

  if (methodReceipt === 'delivery') {
    return 'Укажите адрес доставки';
  } else {
    return 'Выберите филиал';
  }
});

/**
 * Возвращает иконку в зависимости от типа получения товара
 */
const displayIcon = computed(() => {
  const methodReceipt = storeCheckoutForm?.getForm?.method_receipt;

  if (methodReceipt === 'delivery') {
    return 'i-carbon-delivery';
  } else {
    return 'i-tabler-tools-kitchen-2';
  }
});
</script>

<template>
  <header
    v-if="route.path === '/' || route.path === '/favorites'"
    ref="headerEl"
    :class="[
      'header',
      // Фон навешиваем классом, а не переопределяем в CSS: в каждый момент активен
      // ровно один из двух, и спорить за специфичность нечему
      isHeaderTransparent ? 'bg-transparent' : 'bg-white dark:bg-zinc-800',
      { translateY: !showHeader && isFixed },
    ]"
    :style="headerStyle"
  >
    <UContainer
      class="header__wrapper"
      :ui="{
        base: 'mx-auto',
        padding: 'px-4 sm:px-6 lg:px-8',
        constrained: 'max-w-[1920px]',
      }"
    >
      <!-- Логотип по центру: отдельной строкой над поиском и контактами.
           Втиснуть его в середину основной строки нельзя — там уже стоят поиск,
           контакты и кнопки, и «центр» получился бы не по центру. -->
      <div v-if="headerAppearance.logo === 'center'" class="header__logo-band">
        <a href="/" role="link" aria-label="Вернуться на главную">
          <img
            :src="storeInstitution.institution?.visual?.logo_file"
            :alt="storeInstitution.institution?.name || 'Логотип'"
            loading="eager"
            fetchpriority="high"
          />
        </a>
      </div>

      <div class="header__top header-top" :class="{ 'header-top--no-logo': headerAppearance.logo !== 'left' }">
        <a
          v-if="headerAppearance.logo === 'left'"
          class="header-top__logo"
          href="/"
          role="link"
          aria-label="Вернуться на главную"
        >
          <img
            :src="storeInstitution.institution?.visual?.logo_file"
            :alt="storeInstitution.institution?.name || 'Логотип'"
            loading="eager"
            fetchpriority="high"
          />
        </a>

        <div class="header-top__search">
          <UiKitInput
            v-model="storeInstitution.searchQuery"
            variant="flat"
            size="lg"
            radius="lg"
            placeholder="Поиск по меню…"
            clearable
            class="w-full"
            @keydown.enter="goToMenu"
          >
            <template #leading>
              <UIcon name="i-heroicons-magnifying-glass-20-solid" class="w-5 h-5" />
            </template>
          </UiKitInput>
        </div>

        <SocialMadia v-if="width > 1023" />

        <!-- Дополнительные ссылки заведения: «О нас», «Резерв стола» и подобное.
             На узких экранах их место — в мобильном меню, здесь они бы вытеснили контакты. -->
        <nav v-if="width > 1023 && headerAppearance.links.length" class="header-top__links">
          <a
            v-for="link in headerAppearance.links"
            :key="link.href"
            :href="link.href"
            class="header-top__link"
          >
            {{ link.label }}
          </a>
        </nav>

        <div class="header-top__info">
          <div v-if="width > 1023 && storeInstitution?.currentCity?.working_hours?.hours" class="header-top__item">
            <span class="header-top__label">Время работы</span>
            <span class="header-top__value">{{ storeInstitution?.currentCity?.working_hours?.hours }}</span>
          </div>
          <div v-if="width > 1023 && storeInstitution?.currentCity?.address" class="header-top__item">
            <span class="header-top__label">Адрес</span>
            <span class="header-top__value">{{ storeInstitution?.currentCity?.address }}</span>
          </div>
          <div v-if="width > 1023 && storeInstitution?.currentCity?.phone" class="header-top__item">
            <span class="header-top__label">Телефон</span>
            <a :href="`tel:${storeInstitution?.currentCity?.phone}`" class="header-top__value header-top__value--link">
              {{ storeInstitution?.currentCity?.phone }}
            </a>
          </div>
          <div v-if="width > 1023 && (storeInstitution?.branchOptions.length ?? 0) > 1" class="header-top__city">
            <UiKitSelect
              size="sm"
              variant="flat"
              :options="branchSelectOptions"
              :model-value="storeInstitution?.selectedBranchId"
              @update:model-value="
                (value) => {
                  storeInstitution?.chooseBranch(value == null ? null : Number(value));
                }
              "
            />
          </div>
        </div>

        <div class="header-top__buttons">
          <UiColorModeButton v-if="width > 1024" />
          <UiButtonMain
            v-if="width <= 1350"
            label=""
            aria-label="Позвонить"
            class="bg-zinc-950 dark:bg-white dark:text-zinc-950 max-lg:hidden"
            icon="i-heroicons-phone-16-solid"
            size="xl"
            block
            color="sky"
            square
            @click="makeCall"
          />

          <!-- Один бейдж на профиле: уведомления + непрочитанные ответы поддержки.
               Отдельная иконка чата в шапке лишняя — путь в чат уже есть из ЛК -->
          <UChip
            v-if="width >= 1024"
            :show="hasHeaderUnread"
            color="red"
            :text="headerUnreadDisplay"
            position="top-right"
            size="xl"
            :ui="{
              position: { 'top-right': 'top-0 right-0' },
              background: 'bg-{color}-500 dark:bg-{color}-500',
              base: 'text-white dark:text-white font-semibold',
            }"
          >
            <UiButtonMain
              class="bg-zinc-950 dark:bg-white dark:text-zinc-950"
              size="xl"
              block
              color="sky"
              square
              :label="truncatedFirstName || 'Профиль'"
              to="/lk"
            >
              <!-- Человечек прорисовывается штрихом при появлении шапки.
                   Слотом, а не пропом `icon`: проп рендерит иконку CSS-маской,
                   а SVG внутри маски не анимируется. -->
              <template #leading>
                <Icon mode="svg" name="line-md:person" class="w-5 h-5" />
              </template>
            </UiButtonMain>
          </UChip>
        </div>
      </div>
      <div class="header-info-mobile grid grid-cols-[minmax(0,1fr)_2rem] gap-5 lg:hidden cursor-pointer">
        <div
          class="grid grid-cols-[auto_minmax(0,1fr)] gap-2 items-center min-w-0"
          @click="storeModal.open(modalStateEnum.ModalMapYandex)"
        >
          <!-- Иконка в зависимости от типа получения -->
          <UIcon :name="displayIcon" class="w-4 h-4 shrink-0" />
          <div class="flex gap-2 items-center text-xs min-w-0">
            <!-- Адрес в зависимости от типа получения: доставка/самовывоз/ем в зале -->
            <!-- min-w-0 + truncate (без flex-1) → адрес сжимается по контенту и обрезается
                 многоточием, шеврон прижат к концу адреса, аватар не выталкивается (shrink-0) -->
            <span v-if="displayAddress" class="min-w-0 truncate" :title="displayAddress">
              {{ displayAddress }}
            </span>
            <span v-else class="min-w-0 truncate text-gray-400">{{ addressPlaceholder }}</span>
            <UIcon name="i-simple-line-icons-arrow-down" class="w-4 h-4 shrink-0" />
          </div>
        </div>
        <nuxt-link to="/lk" class="relative">
          <UChip
            :show="hasHeaderUnread"
            color="red"
            :text="headerUnreadDisplay"
            position="top-right"
            size="lg"
            :ui="{
              position: { 'top-right': 'top-0 right-0' },
              background: 'bg-{color}-500 dark:bg-{color}-500',
              base: 'text-white dark:text-white font-semibold',
            }"
          >
            <UAvatar :src="imgAvatar" alt="Avatar" />
          </UChip>
        </nuxt-link>
      </div>

      <div class="header__bottom header-bottom">
        <nav
          v-if="width < 1536"
          ref="navRef"
          class="header__nav header-nav flex gap-3 pt-2 pl-2 pb-2 pr-2 overflow-x-auto whitespace-nowrap"
        >
          <ProductTagsFilter
            :available-tags="storeInstitution.productTags"
            :selected-tags="storeInstitution.selectedTagIds"
            :results-count="storeInstitution.totalProductsCount"
            size="sm"
            @update:selected-tags="storeInstitution.selectedTagIds = $event"
            @reset="storeInstitution.selectedTagIds = []"
          />

          <UiButtonMain
            v-if="storeAuth.isLoggedIn"
            class="py-1 px-2 bg-sky-300 text-black dark:text-white rounded-lg header-nav__link flex-shrink-0 dark:bg-zinc-950 dark:border-zinc-600"
            size="xl"
            color="sky"
            square
            label="Избранные"
            to="/favorites"
          />
          <button
            v-for="(category, index) in getCategories()"
            :key="`${route.path}-${index}`"
            role="button"
            :data-link="category.to"
            class="py-1 px-2 bg-sky-300 rounded-lg header-nav__link flex-shrink-0 dark:bg-zinc-950 dark:border-zinc-600"
            :class="{ '!bg-red-700 text-white': isActive(category.to) }"
            @click="handleCategoryClick(category.to)"
          >
            {{ category.category }}
          </button>
        </nav>
      </div>
    </UContainer>
  </header>
</template>

<style scoped lang="scss">
.header {
  // Фон шапки живёт в assets/css/appearance.scss — вместе с остальным
  // конфигом внешнего вида и, главное, вне scoped-стилей: в этом SFC они
  // ведут себя ненадёжно (правило попадает в разметку, но не применяется).
  @apply lg:pt-5 fixed top-0 left-0 w-full z-40 max-md:pb-0 lg:pb-5;

  &.translateY {
    @apply -mt-16 md:-mt-20 lg:mt-0;
  }

  &__wrapper {
  }

  // Логотип по центру — отдельной строкой над основной
  &__logo-band {
    @apply max-lg:hidden flex justify-center pb-3;

    img {
      @apply max-h-[50px] w-auto object-contain;
    }
  }

  &-top {
    @apply grid grid-cols-[1fr_auto] lg:grid-cols-[auto_auto_1fr_auto] 2xl:grid-cols-[auto_1fr_auto_auto_auto] gap-5 items-center;

    // Логотипа в строке нет (он по центру или скрыт) — колонок на одну меньше,
    // иначе на его месте остаётся пустой столбец и всё съезжает влево
    &--no-logo {
      @apply lg:grid-cols-[auto_1fr_auto] 2xl:grid-cols-[1fr_auto_auto_auto];
    }

    &__links {
      @apply flex items-center gap-4 whitespace-nowrap;
    }

    &__link {
      @apply text-sm font-medium text-gray-600 dark:text-gray-300 transition-colors;

      &:hover {
        color: var(--app-accent, #ef4444);
      }
    }

    &__logo {
      @apply z-50 max-lg:hidden max-w-[80px] h-[50px] flex items-center justify-center;

      img {
        @apply max-h-full max-w-full object-contain;
      }
    }

    &__search {
      @apply max-2xl:hidden;

      /* В тёмной теме flat-инпут (#27272a) совпадает с фоном шапки (zinc-800 = #27272a)
         и поиск становится невидимым → поднимаем фон поля на тон выше. */
      :deep(.group) {
        @apply dark:bg-zinc-700 dark:hover:bg-zinc-600 dark:focus-within:bg-zinc-600;
      }
    }

    &__item {
      @apply flex flex-col gap-0.5 leading-tight;
    }

    /* Контакты в шапке: от 1024 до 1279px строка тесная (логотип, три блока контактов,
       выбор филиала и кнопки), и значения переносились по слогам — «+7 (918) 973-11-/93»,
       «Широкая /32». Номер телефона, разорванный посередине, читается как опечатка,
       поэтому переносы запрещаем, а место освобождаем кеглем: на узком экране подпись
       и значение на ступень мельче. С 1280px возвращается прежний размер. */
    &__label {
      @apply text-[10px] xl:text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500;
      @apply whitespace-nowrap tracking-normal xl:tracking-wider;
    }

    &__value {
      @apply text-xs xl:text-sm font-semibold text-gray-900 dark:text-white;
      @apply whitespace-nowrap;

      &--link {
        @apply transition-colors hover:text-sky-600 dark:hover:text-sky-400;
      }
    }

    &__buttons {
      @apply grid grid-cols-[1fr_auto] lg:grid-cols-[1fr_auto_auto] items-center gap-2;
    }

    &__info {
      // Зазор тоже сжимаем до 1280px — вместе с кеглем это те самые ~60px,
      // которых не хватало строке, чтобы не переноситься
      @apply flex gap-3 xl:gap-5 items-center max-lg:hidden;
    }

    &__city {
      // Ширина по контенту (до 360px), чтобы стрелка прижималась к концу адреса,
      // а длинное название всё равно помещалось (сверх лимита — обрезка по тексту).
      @apply w-fit max-w-[360px] shrink-0;

      :deep([role='button']) {
        @apply gap-3;
      }
    }
  }

  &-bottom {
    @apply overflow-y-auto;
  }

  &__filters {
    @apply border-t border-gray-200 dark:border-gray-700 pt-3;

    /* Sticky на мобильных для удобства */
    @media (max-width: 1023px) {
      @apply sticky bg-white dark:bg-zinc-800 z-30;
      top: var(--safe-area-inset-top, 0);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }
  }
}

</style>
